import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Leaf, Drop, Sun, ArrowLeft, ArrowRight, CheckCircle, Warning,
  ListChecks, Storefront, ShieldCheck, CircleNotch, Info, UsersThree, Copy,
  Printer,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { http } from "@/lib/api";
import { fpPath } from "@/lib/site";

/**
 * Climate-Smart Farming — Release 1 hub.
 *
 * FarmersPulse position (locked): neutral farmer-support layer.
 * NOT a credit issuer. Every climate number is an estimate until an
 * independent verifier confirms it — the UI reinforces that in
 * every place a figure appears.
 */
const TABS = [
  { key: "practices",    label: "Practices",     icon: Leaf },
  { key: "awd",          label: "AWD Wizard",    icon: Drop },
  { key: "log",          label: "Field Log",     icon: ListChecks },
  { key: "season",       label: "Season",        icon: Sun },
  { key: "opportunities",label: "Opportunities", icon: Storefront },
  { key: "consent",      label: "My Enrolments", icon: ShieldCheck },
  { key: "coop",         label: "Cooperative",   icon: UsersThree },
];

export default function FarmersClimate() {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "practices");
  useEffect(() => { setSp((p) => { p.set("tab", tab); return p; }, { replace: true }); }, [tab, setSp]);

  return (
    <main
      className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]"
      data-testid="farmers-climate"
      data-translate-target="true"
    >
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="climate-back">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        <header className="mt-3" data-testid="climate-header">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            FarmersPulse · Climate-Smart Farming
          </div>
          <h1 className="mt-1 font-serif-display text-4xl sm:text-5xl leading-[1.02] tracking-tight text-[#151312]">
            Adopt, document and benefit — without waiting on promises.
          </h1>
          <p className="mt-3 text-lg text-[#3A3830] max-w-3xl">
            FarmersPulse is a neutral farmer-support layer. We help you find climate-smart practices, log evidence and compare vetted programmes.
            <strong className="text-[#151312]"> We do not issue, certify or sell carbon credits.</strong> Every climate figure here is an ESTIMATE until an independent verifier confirms it.
          </p>
        </header>

        <div className="mt-4">
          <ListenTranslateBar text="Climate-Smart Farming from FarmersPulse. Adopt, document and benefit without waiting on promises. FarmersPulse is a neutral farmer-support layer, not a credit issuer. Every climate figure here is an estimate until an independent verifier confirms it." testidPrefix="climate-tools" />
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="climate-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`climate-tab-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${tab === key ? "bg-[#2D8659] text-white border-transparent" : "border-black/15 bg-white text-[#241209] hover:bg-black/5"}`}
            >
              <Icon size={12} weight="fill" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "practices"     && <PracticesTab onOpen={setTab} />}
          {tab === "awd"           && <AwdTab onDone={() => setTab("log")} />}
          {tab === "log"           && <FieldLogTab />}
          {tab === "season"        && <SeasonTab />}
          {tab === "opportunities" && <OpportunitiesTab onEnrolled={() => setTab("consent")} />}
          {tab === "consent"       && <ConsentTab />}
          {tab === "coop"          && <CoopTab />}
        </div>
      </div>
    </main>
  );
}

const EstimateBadge = () => (
  <span data-testid="estimate-badge" className="inline-flex items-center gap-1 rounded-full bg-[#F9C784]/40 border border-[#B85F2A]/30 text-[#7A3E12] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase">
    <Info size={10} weight="fill" /> Estimate
  </span>
);

function Loading() {
  return <div className="mt-4 flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A]"><CircleNotch size={14} className="animate-spin" /> Loading…</div>;
}

// ─────────────────────────────────────────────────────────────
// 1. Practices
// ─────────────────────────────────────────────────────────────
function PracticesTab({ onOpen }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => { http.get("/climate/practices").then((r) => setList(r?.data?.practices || [])).finally(() => setBusy(false)); }, []);

  if (busy) return <Loading />;
  return (
    <section className="grid md:grid-cols-2 gap-4" data-testid="climate-practices">
      {list.map((p) => (
        <div key={p.practice_id} className="rounded-2xl bg-white border border-black/10 p-5" data-testid={`practice-${p.practice_id}`}>
          <div className="flex items-baseline gap-2">
            <div className="font-serif-display text-xl md:text-2xl leading-tight">{p.name}</div>
            <EstimateBadge />
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-1">Crop · {p.crop}</div>
          {p.expected_benefit_ranges && (
            <ul className="mt-3 grid grid-cols-2 gap-2">
              {Object.entries(p.expected_benefit_ranges).slice(0, 4).map(([k, v]) => (
                <li key={k} className="rounded-lg bg-[#F9F5EC] p-2 text-[12px] text-[#241209]">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#8A857A]">{k.replace(/_/g, " ")}</div>
                  {Array.isArray(v) ? `${v[0]}%–${v[1]}%` : v}
                </li>
              ))}
            </ul>
          )}
          {p.risks && p.risks.length > 0 && (
            <div className="mt-3 text-[12px] text-[#7A3E12] flex items-start gap-1.5">
              <Warning size={13} weight="fill" className="mt-0.5 flex-none" />
              <span>Risks: {p.risks.join(" · ")}</span>
            </div>
          )}
          {p.practice_id === "rice-awd" && (
            <button onClick={() => onOpen("awd")} data-testid="practices-open-awd" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
              Check AWD suitability <ArrowRight size={11} weight="bold" />
            </button>
          )}
        </div>
      ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. AWD Wizard
// ─────────────────────────────────────────────────────────────
function AwdTab({ onDone }) {
  const [meta, setMeta] = useState({ questions: [], answers: [] });
  const [answers, setAnswers] = useState({});
  const [field, setField] = useState("");
  const [busy, setBusy] = useState(true);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get("/climate/awd/wizard").then((r) => setMeta(r.data)).finally(() => setBusy(false)); }, []);

  async function submit() {
    if (Object.keys(answers).length !== meta.questions.length) { setErr("Answer every question honestly."); return; }
    setErr("");
    try {
      const { data } = await http.post("/climate/awd/check", { field_name: field, answers });
      setResult(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Sign in first — checks are saved to your farm."); }
  }

  if (busy) return <Loading />;
  if (result) return (
    <section className="rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 md:p-8" data-testid="awd-result">
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">AWD suitability</div>
      <div className="font-serif-display text-3xl mt-1 leading-tight">
        {result.verdict === "suitable" && "Looks suitable for AWD."}
        {result.verdict === "with_caution" && "Suitable — with caution."}
        {result.verdict === "not_yet" && "Not yet — build up prerequisites first."}
        {result.verdict === "not_suitable" && "Not suitable right now."}
      </div>
      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#EFE3CE] mt-2">Score · {result.score_pct}%</div>
      {result.blockers?.length > 0 && (
        <ul className="mt-3 space-y-1 text-[13px] text-[#F9C784]">{result.blockers.map((b, i) => <li key={i}>⚠ {b}</li>)}</ul>
      )}
      <p className="mt-4 text-[13px] text-[#EFE3CE]">This is a first-look screener. A local agronomist should confirm before you adopt AWD — practice rules vary by soil, water and region.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={onDone} data-testid="awd-goto-log" className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Start the field log <ArrowRight size={11} weight="bold" /></button>
        <button onClick={() => setResult(null)} data-testid="awd-retake" className="inline-flex items-center gap-1.5 rounded-full bg-white/15 text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Retake</button>
      </div>
    </section>
  );

  return (
    <section data-testid="awd-wizard">
      <div className="rounded-3xl bg-white border border-black/10 p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">6 questions · about 2 minutes</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1">AWD Suitability Check</div>
        <input value={field} onChange={(e) => setField(e.target.value)} placeholder="Field name (optional)" data-testid="awd-field-name" className="mt-3 w-full rounded-lg border border-black/15 px-3 py-2 text-sm" />
      </div>
      <div className="mt-4 grid gap-3">
        {meta.questions.map((q) => (
          <div key={q.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`awd-q-${q.id}`}>
            <div className="text-[15px] text-[#151312]">{q.q}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {meta.answers.map((a) => (
                <button key={a.key} type="button" onClick={() => setAnswers((s) => ({ ...s, [q.id]: a.key }))} data-testid={`awd-${q.id}-${a.key}`}
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition ${answers[q.id] === a.key ? "bg-[#2D8659] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
                >{a.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{err}</div>}
      <button onClick={submit} data-testid="awd-submit" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase">
        See my verdict <ArrowRight size={12} weight="bold" />
      </button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. Field Log
// ─────────────────────────────────────────────────────────────
function FieldLogTab() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [form, setForm] = useState({ field_name: "", practice_id: "rice-awd", event_type: "irrigate", water_depth_cm: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function load() { setBusy(true); try { const { data } = await http.get("/climate/field-log"); setLogs(data?.logs || []); } catch { setLogs([]); } setBusy(false); }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await http.post("/climate/field-log", {
        field_name: form.field_name || null,
        practice_id: form.practice_id,
        event_type: form.event_type,
        water_depth_cm: form.water_depth_cm ? Number(form.water_depth_cm) : null,
        note: form.note || null,
      });
      setForm({ ...form, water_depth_cm: "", note: "" });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Sign in first."); }
    setSaving(false);
  }

  return (
    <section data-testid="climate-log">
      <form onSubmit={save} className="rounded-3xl bg-white border border-black/10 p-5 grid gap-3" data-testid="climate-log-form">
        <div className="font-serif-display text-xl">Log a field event</div>
        <input value={form.field_name} onChange={(e) => setForm({ ...form, field_name: e.target.value })} placeholder="Field name" data-testid="log-field" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.practice_id} onChange={(e) => setForm({ ...form, practice_id: e.target.value })} data-testid="log-practice" className="rounded-lg border border-black/15 px-3 py-2 text-sm">
            <option value="rice-awd">Rice AWD</option>
            <option value="direct-seeded-rice">Direct-seeded rice</option>
            <option value="laser-land-leveling">Laser land leveling</option>
            <option value="residue-management">Residue management</option>
            <option value="efficient-fertilizer">Efficient fertilizer</option>
            <option value="solar-pumping">Solar pumping</option>
          </select>
          <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="log-event" className="rounded-lg border border-black/15 px-3 py-2 text-sm">
            <option value="irrigate">Irrigate / reflood</option>
            <option value="drain">Drying / drain</option>
            <option value="photo">Photo evidence</option>
            <option value="note">Note</option>
          </select>
        </div>
        <input value={form.water_depth_cm} onChange={(e) => setForm({ ...form, water_depth_cm: e.target.value })} placeholder="Water tube depth (cm)" type="number" data-testid="log-depth" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
        <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" data-testid="log-note" rows={2} className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
        {err && <div className="text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{err}</div>}
        <button type="submit" disabled={saving} data-testid="log-save" className="justify-self-start inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
          {saving ? "Saving…" : "Save event"} <ArrowRight size={11} weight="bold" />
        </button>
      </form>

      <div className="mt-6 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A]">Recent events</div>
      {busy && <Loading />}
      <div className="mt-2 grid gap-2">
        {logs.map((l) => (
          <div key={l.id} className="rounded-xl bg-white border border-black/10 p-3 text-sm" data-testid={`log-${l.id}`}>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A]">{new Date(l.created_at).toLocaleString()} · {l.practice_id} · {l.event_type}</div>
            <div className="text-[#241209]">
              {l.field_name && <span className="font-medium">{l.field_name} · </span>}
              {l.water_depth_cm != null && <>Depth {l.water_depth_cm} cm · </>}
              {l.note}
            </div>
          </div>
        ))}
        {!busy && logs.length === 0 && <div className="text-sm text-[#8A857A]">No events yet — save your first irrigation event above.</div>}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. Season Summary
// ─────────────────────────────────────────────────────────────
function SeasonTab() {
  const [practice, setPractice] = useState("rice-awd");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => { setBusy(true); http.get("/climate/season/summary", { params: { practice_id: practice } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setBusy(false)); }, [practice]);

  return (
    <section data-testid="climate-season">
      <div className="flex flex-wrap gap-1">
        {["rice-awd","direct-seeded-rice","laser-land-leveling","residue-management","efficient-fertilizer","solar-pumping"].map((p) => (
          <button key={p} onClick={() => setPractice(p)} data-testid={`season-tab-${p}`}
            className={`rounded-full border px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase ${practice===p?"bg-[#2D8659] text-white border-transparent":"border-black/12 bg-white text-[#3A3830]"}`}
          >{p.replace(/-/g," ")}</button>
        ))}
      </div>
      {busy && <Loading />}
      {data && (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Events logged"    value={data.events_logged} />
            <Stat label="Irrigation events" value={data.irrigation_events} />
            <Stat label="Drying events"    value={data.drying_events} />
            <Stat label="Avg water depth"  value={data.avg_water_depth_cm != null ? `${data.avg_water_depth_cm} cm` : "—"} />
          </div>
          <div className="mt-4 rounded-3xl bg-white border border-[#B85F2A]/30 p-5" data-testid="season-impact">
            <div className="flex items-center gap-2">
              <EstimateBadge />
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Estimated impact ranges</div>
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-2">
              {Object.entries(data.estimated_impact?.ranges || {}).map(([k, v]) => (
                <li key={k} className="rounded-lg bg-[#F9F5EC] p-2 text-[12px]">
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#8A857A]">{k.replace(/_/g," ")}</div>
                  {Array.isArray(v) ? `${v[0]}%–${v[1]}%` : v}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[12px] italic text-[#7A3E12]">{data.estimated_impact?.disclaimer}</p>
          </div>
        </>
      )}
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl bg-white border border-black/10 p-4">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">{label}</div>
    <div className="mt-1 font-serif-display text-2xl">{value ?? 0}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// 5. Opportunity Directory
// ─────────────────────────────────────────────────────────────
function OpportunitiesTab({ onEnrolled }) {
  const [opps, setOpps] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(null);
  useEffect(() => { http.get("/climate/opportunities").then((r) => setOpps(r?.data?.opportunities || [])).finally(() => setBusy(false)); }, []);

  if (open) return <OpportunityDetail opp={open} onBack={() => setOpen(null)} onEnrolled={() => { setOpen(null); onEnrolled(); }} />;
  if (busy) return <Loading />;

  return (
    <section data-testid="climate-opportunities">
      <p className="text-[13px] text-[#3A3830]">Every listing shows fees, farmer share, obligations, data permissions and grievance channels. Nothing is ranked by advertised payout.</p>
      <div className="mt-3 grid md:grid-cols-2 gap-3">
        {opps.map((o) => (
          <button key={o.id} onClick={() => setOpen(o)} data-testid={`opp-${o.id}`} className="text-left rounded-2xl bg-white border border-black/10 p-5 hover:border-[#2D8659] transition">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{o.provider}</div>
            <div className="font-serif-display text-lg md:text-xl leading-tight mt-1">{o.title}</div>
            <div className="mt-2 text-[12px] text-[#3A3830]">Methodology · {o.methodology || "Disclosed at enrolment"}</div>
            <div className="mt-1 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[#8A857A]">Review · {o.fp_review_status?.replace(/_/g," ")}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

function OpportunityDetail({ opp, onBack, onEnrolled }) {
  const scopes = ["location","identity","financial","media","satellite","research","partner_transfer","public_stories","marketing"];
  const [consents, setConsents] = useState(Object.fromEntries(scopes.map((s) => [s, false])));
  const [field, setField] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function enroll() {
    setBusy(true); setErr("");
    try { await http.post("/climate/enroll", { opportunity_id: opp.id, field_name: field || null, consents }); onEnrolled(); }
    catch (e) { setErr(e?.response?.data?.detail || "Sign in first."); }
    setBusy(false);
  }

  return (
    <section data-testid="opp-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        <ArrowLeft size={11} weight="bold" /> All opportunities
      </button>
      <div className="rounded-3xl bg-white border border-black/10 p-6">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{opp.provider}</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">{opp.title}</div>
        <div className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          <Row k="Methodology" v={opp.methodology} />
          <Row k="Verifier" v={opp.verifier} />
          <Row k="Contract length" v={opp.contract_length_years} />
          <Row k="Exclusivity" v={opp.exclusivity} />
          <Row k="Farmer share" v={opp.farmer_share_pct} />
          <Row k="Fees" v={opp.fees} />
          <Row k="Payment timing" v={opp.payment_timing} />
          <Row k="Exit terms" v={opp.exit_terms} />
        </div>
        <p className="mt-3 text-[12px] italic text-[#7A3E12]">This directory listing is informational. Read the full contract from the provider before signing.</p>
      </div>
      <div className="mt-5 rounded-3xl bg-white border border-black/10 p-6">
        <div className="font-serif-display text-xl">Enrol interest — with granular consent</div>
        <p className="text-[12px] text-[#3A3830] mt-1">You control which data leaves FarmersPulse. Turn on only what you're comfortable sharing. You can update these any time.</p>
        <input value={field} onChange={(e) => setField(e.target.value)} placeholder="Field name (optional)" data-testid="opp-field-name" className="mt-3 w-full rounded-lg border border-black/15 px-3 py-2 text-sm" />
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {scopes.map((s) => (
            <label key={s} className="flex items-start gap-2 text-[13px]">
              <input type="checkbox" checked={consents[s]} onChange={(e) => setConsents({ ...consents, [s]: e.target.checked })} data-testid={`consent-${s}`} className="mt-1 h-4 w-4 accent-[#2D8659]" />
              <span>Allow sharing my <strong>{s.replace(/_/g," ")}</strong> data with this programme.</span>
            </label>
          ))}
        </div>
        {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{err}</div>}
        <button onClick={enroll} disabled={busy} data-testid="opp-enroll" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase disabled:opacity-60">
          {busy ? "Enrolling…" : "Enrol interest"} <CheckCircle size={12} weight="fill" />
        </button>
      </div>
    </section>
  );
}

const Row = ({ k, v }) => (
  <div>
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">{k}</div>
    <div className="text-[13px] text-[#241209]">{v || "Disclosed at enrolment"}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// 6. Consent + Enrolments
// ─────────────────────────────────────────────────────────────
function ConsentTab() {
  const [data, setData] = useState({ enrollments: [], consent_scopes: [], states: [] });
  const [busy, setBusy] = useState(true);
  const load = () => { setBusy(true); http.get("/climate/enrollments/mine").then((r) => setData(r.data)).catch(() => setData({ enrollments: [], consent_scopes: [], states: [] })).finally(() => setBusy(false)); };
  useEffect(load, []);

  async function toggle(enrol_id, scope, allowed, existing) {
    try {
      const next = { ...existing, [scope]: allowed };
      await http.post(`/climate/enrollments/${enrol_id}/consent`, { consents: next });
      load();
    } catch {}
  }

  if (busy) return <Loading />;
  if (!data.enrollments.length) return <div className="text-sm text-[#8A857A]" data-testid="no-enrolments">No enrolments yet — pick an opportunity to get started.</div>;

  return (
    <section data-testid="climate-consent" className="grid gap-4">
      {data.enrollments.map((e) => (
        <div key={e.id} className="rounded-3xl bg-white border border-black/10 p-5" data-testid={`enrol-${e.id}`}>
          <div className="flex flex-wrap items-baseline gap-3">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{e.provider}</div>
              <div className="font-serif-display text-lg md:text-xl">{e.field_name || "No field assigned"}</div>
            </div>
            <div className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#151312]">Status · {e.state?.replace(/_/g," ")}</div>
          </div>
          <div className="mt-3">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A] mb-1">Consent scopes · v{e.consent_version}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {(data.consent_scopes || []).map((s) => (
                <label key={s} className="flex items-start gap-2 text-[12px]">
                  <input type="checkbox" checked={!!e.consents?.[s]} onChange={(ev) => toggle(e.id, s, ev.target.checked, e.consents || {})} data-testid={`enrol-consent-${e.id}-${s}`} className="mt-0.5 h-4 w-4 accent-[#2D8659]" />
                  <span>{s.replace(/_/g," ")}</span>
                </label>
              ))}
            </div>
          </div>
          <p className="mt-3 text-[11px] italic text-[#7A3E12]">Nothing has been verified or paid until the state moves past <strong>validated</strong>. Every state change is recorded in the audit log.</p>
        </div>
      ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// 7. Cooperative Dashboard
// ─────────────────────────────────────────────────────────────
function CoopTab() {
  const [mships, setMships] = useState([]);
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: "", region: "", description: "" });
  const [joinForm, setJoinForm] = useState({ invite_code: "", field_name: "", role: "member" });
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setBusy(true);
    http.get("/climate/coop/mine")
      .then((r) => setMships(r.data?.memberships || []))
      .catch(() => setMships([]))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);

  async function create(e) {
    e.preventDefault(); setErr("");
    try { await http.post("/climate/coop/create", form); setForm({ name: "", region: "", description: "" }); setCreating(false); load(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Sign in first — cooperatives need a farmer profile."); }
  }
  async function join(e) {
    e.preventDefault(); setErr("");
    try { await http.post("/climate/coop/join", { ...joinForm, invite_code: joinForm.invite_code.trim().toUpperCase() }); setJoinForm({ invite_code: "", field_name: "", role: "member" }); setJoining(false); load(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Sign in first — cooperatives need a farmer profile."); }
  }

  if (openId) return <CoopDashboard coopId={openId} onBack={() => setOpenId(null)} />;

  return (
    <section data-testid="climate-coop">
      <div className="rounded-3xl bg-white border border-black/10 p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Cooperative dashboard</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">Roll up member evidence at a glance.</div>
        <p className="text-[13px] text-[#3A3830] mt-1">Field agents see aggregate AWD adoption across every member farm and can spot evidence gaps before a verifier does.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => { setCreating((s) => !s); setJoining(false); }} data-testid="coop-toggle-create"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
            {creating ? "Cancel" : "Start a cooperative"}
          </button>
          <button onClick={() => { setJoining((s) => !s); setCreating(false); }} data-testid="coop-toggle-join"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/15 text-[#241209] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
            {joining ? "Cancel" : "Join with invite code"}
          </button>
        </div>
        {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="coop-err">{err}</div>}
      </div>

      {creating && (
        <form onSubmit={create} className="mt-4 rounded-2xl bg-white border border-black/10 p-4 grid gap-2" data-testid="coop-create-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cooperative name" required data-testid="coop-name" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
          <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Region (optional)" data-testid="coop-region" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this co-op is for (optional)" rows={2} data-testid="coop-desc" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
          <button type="submit" data-testid="coop-create-save" className="justify-self-start inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Create cooperative <ArrowRight size={11} weight="bold" /></button>
        </form>
      )}
      {joining && (
        <form onSubmit={join} className="mt-4 rounded-2xl bg-white border border-black/10 p-4 grid gap-2" data-testid="coop-join-form">
          <input value={joinForm.invite_code} onChange={(e) => setJoinForm({ ...joinForm, invite_code: e.target.value })} placeholder="Invite code (e.g. AB12CD34)" required data-testid="coop-invite" className="rounded-lg border border-black/15 px-3 py-2 text-sm uppercase tracking-widest" />
          <input value={joinForm.field_name} onChange={(e) => setJoinForm({ ...joinForm, field_name: e.target.value })} placeholder="Your field name (optional)" data-testid="coop-field" className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
          <select value={joinForm.role} onChange={(e) => setJoinForm({ ...joinForm, role: e.target.value })} data-testid="coop-role" className="rounded-lg border border-black/15 px-3 py-2 text-sm">
            <option value="member">Join as farmer (member)</option>
            <option value="field_agent">Join as field agent (steward)</option>
          </select>
          <button type="submit" data-testid="coop-join-save" className="justify-self-start inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Join cooperative <ArrowRight size={11} weight="bold" /></button>
        </form>
      )}

      <div className="mt-6 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A]">My cooperatives</div>
      {busy && <Loading />}
      {!busy && mships.length === 0 && (
        <div className="mt-2 text-sm text-[#8A857A]" data-testid="coop-empty">You're not part of any cooperative yet. Start one or join with an invite code above.</div>
      )}
      <div className="mt-2 grid md:grid-cols-2 gap-3">
        {mships.map((m) => (
          <div key={m.coop.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`coop-${m.coop.id}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{m.role?.replace(/_/g, " ")}</div>
                <div className="font-serif-display text-lg md:text-xl leading-tight">{m.coop.name}</div>
                {m.coop.region && <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#8A857A] mt-0.5">{m.coop.region}</div>}
              </div>
              <div className="ml-auto flex items-center gap-1 text-[11px] font-mono-tech text-[#241209] bg-[#F9F5EC] px-2 py-1 rounded-full" data-testid={`coop-code-${m.coop.id}`}>
                <span className="tracking-widest">{m.coop.invite_code}</span>
                <button type="button" onClick={() => { try { navigator.clipboard?.writeText(m.coop.invite_code); } catch {} }} className="ml-1 opacity-60 hover:opacity-100" aria-label="Copy invite code"><Copy size={11} weight="bold" /></button>
              </div>
            </div>
            {m.coop.description && <p className="text-[13px] text-[#3A3830] mt-2">{m.coop.description}</p>}
            {m.role === "field_agent" && (
              <button onClick={() => setOpenId(m.coop.id)} data-testid={`coop-open-${m.coop.id}`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
                Open dashboard <ArrowRight size={11} weight="bold" />
              </button>
            )}
            {m.role === "member" && (
              <p className="mt-3 text-[11px] italic text-[#8A857A]">Your field agent sees your logged events — your consent controls what leaves this app.</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function CoopDashboard({ coopId, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [practice, setPractice] = useState("rice-awd");
  const [err, setErr] = useState("");
  const load = () => {
    setBusy(true); setErr("");
    http.get(`/climate/coop/${coopId}/dashboard`, { params: { practice_id: practice } })
      .then((r) => setData(r.data))
      .catch((ex) => setErr(ex?.response?.data?.detail || "Unable to load dashboard."))
      .finally(() => setBusy(false));
  };
  useEffect(load, [coopId, practice]);

  function openSnapshot() {
    const api = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${api}/api/climate/coop/${coopId}/snapshot.html?practice_id=${encodeURIComponent(practice)}`;
    window.open(url, "_blank", "noopener");
  }

  return (
    <section data-testid="coop-dashboard">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        <ArrowLeft size={11} weight="bold" /> All cooperatives
      </button>
      {busy && <Loading />}
      {err && <div className="text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="coop-dashboard-err">{err}</div>}
      {data && (
        <>
          <div className="rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 relative">
            <button type="button" onClick={openSnapshot} data-testid="coop-snapshot"
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-95">
              <Printer size={12} weight="fill" /> Save as PDF
            </button>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">Cooperative · {data.practice_id.replace(/-/g, " ")}</div>
            <div className="font-serif-display text-3xl mt-1 leading-tight">{data.coop.name}</div>
            {data.coop.region && <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#EFE3CE] mt-1">{data.coop.region}</div>}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat label="Members" value={data.totals.members} />
              <MiniStat label="Events" value={data.totals.events} />
              <MiniStat label="Irrigation" value={data.totals.irrigation} />
              <MiniStat label="Photos" value={data.totals.photos} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-mono-tech tracking-[0.24em] uppercase">
              <span className="rounded-full bg-[#2D8659]/40 px-2 py-0.5" data-testid="coop-green">{data.totals.gaps_green} on track</span>
              <span className="rounded-full bg-[#F9C784]/30 px-2 py-0.5" data-testid="coop-amber">{data.totals.gaps_amber} watch</span>
              <span className="rounded-full bg-[#B85F2A]/50 px-2 py-0.5" data-testid="coop-red">{data.totals.gaps_red} evidence gap</span>
            </div>
            <p className="mt-3 text-[12px] italic text-[#F9C784]">{data.estimated_impact_note}</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-1" data-testid="coop-practice-picker">
            {["rice-awd","direct-seeded-rice","residue-management","efficient-fertilizer","solar-pumping","laser-land-leveling"].map((p) => (
              <button key={p} onClick={() => setPractice(p)} data-testid={`coop-practice-${p}`}
                className={`rounded-full border px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase ${practice === p ? "bg-[#2D8659] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830]"}`}>{p.replace(/-/g, " ")}</button>
            ))}
          </div>

          <div className="mt-4 grid gap-2">
            {data.farms.length === 0 && <div className="text-sm text-[#8A857A]">No members yet — share the invite code so farmers can join.</div>}
            {data.farms.map((f) => (
              <div key={f.user_id} className="rounded-2xl bg-white border border-black/10 p-4 flex flex-wrap gap-3 items-start" data-testid={`coop-farm-${f.user_id}`}>
                <div className="flex-none w-2.5 h-2.5 rounded-full mt-2" style={{ background: f.status === "red" ? "#B85F2A" : f.status === "amber" ? "#F9C784" : "#2D8659" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg leading-tight">{f.field_name}</div>
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-0.5">
                    {f.events} events · {f.irrigation} irrigation · {f.drying} drying · {f.photos} photos {f.avg_depth_cm != null ? `· avg ${f.avg_depth_cm} cm` : ""}
                  </div>
                  {f.last_event_at && (
                    <div className="text-[11px] text-[#8A857A] mt-0.5">Last event · {new Date(f.last_event_at).toLocaleDateString()}</div>
                  )}
                  {f.gaps?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {f.gaps.map((g) => (
                        <span key={g} className="text-[10px] font-mono-tech tracking-[0.22em] uppercase bg-[color:var(--np-brand,#B85F2A)]/15 text-[#7A3E12] px-2 py-0.5 rounded-full">{g}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl bg-white/10 border border-white/15 p-3">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#F9C784]">{label}</div>
    <div className="mt-1 font-serif-display text-2xl">{value ?? 0}</div>
  </div>
);
